import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Teacher = {
  _id: string
  email: string
  firstName: string
  lastName: string
}

export type Section = {
  _id: string
  name: string
  branch: string
  status: string
  createdAt: string
  createdBy: string
  updatedAt: string
}

export type Session = {
  _id: string
  title: string
  startDate: string
  endDate: string
  isActive: boolean
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export type Branch = {
  _id: string
  name: string
  address: string
  campusNo: string
  contactNumber: string
  email: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type Course = {
  _id: string
  name: string
  code: string
  teacher: string
  class?: string
  createdAt?: string
  createdBy?: string
}

export type ClassType = {
  _id: string
  name: string
  sections: string[]
  branch: string
  capacity: number
  classIdentifier: string
  course: string[]
  createdAt: string
  createdBy: string
  order: number
  registrationType: string
  status: string
  students: string[]
  subjects: string[]
  system: string
  updatedAt: string
}

export type Assignment = {
  _id: string
  title: string
  type: "assignment" | "project" | "exam" | "other"
  class: ClassType
  course: Course | null
  submission_date?: string
  due_date?: string
  branch_id: Branch | null
  file_url?: string
  feedback?: string
  grade?: string
  status: "submitted" | "pending" | "graded"
  createdAt: string
  updatedAt: string
  assignmentIdentifier: string
  section: Section
}

export type AssignmentCreateData = {
  title: string
  type: "assignment" | "project" | "exam" | "other"
  class: string
  course: string
  submission_date?: string
  due_date?: string
  branch_id?: string
  feedback?: string
  grade?: string
  section?: string
  session?: string
}

export type AssignmentUpdateData = Partial<AssignmentCreateData> & {
  status?: "submitted" | "pending" | "graded"
}

export type Note = {
  _id: string
  title: string 
  teacher: {
    _id: string
    firstName: string
    lastName: string
  }
  class: ClassType
  section: Section
  course: Course | null
  session: Session
  role: "course" | "homeroom"
  branch?: string
  file_url?: string
   status: "active" | "inactive"
  createdAt: string
  updatedAt: string
  notesIdentifier: string
}

export type NoteCreateData = {
  title: string
  teacher: string
  class: string
  section: string
  course: string
  role?: "course" | "homeroom"
  branch?: string
  session?: string
}

export type NoteUpdateData = Partial<NoteCreateData> & {
  status?: "active" | "inactive"
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type AssignmentsResponse = {
  success: boolean
  data: Assignment[]
  pagination: PaginationData
  message?: string
}

export type AssignmentResponse = {
  success: boolean
  data: Assignment
  message?: string
}

export type NotesResponse = {
  success: boolean
  data: Note[]
  pagination: PaginationData
  message?: string
}

export type NoteResponse = {
  success: boolean
  data: Note
  message?: string
}

const ASSIGNMENT_ENDPOINT = {
  ASSIGNMENTS: "/assignment",
}

const NOTES_ENDPOINT = {
  NOTES: "/notes",
}

const COURSES_ENDPOINT = {
  COURSES: "/courses",
}

const getCoursesByClass = async (classId: string): Promise<{ success: boolean; data: Course[] }> => {
  try {
    // Fetch all courses from courses API
    const { getAllCourses } = await import("../courses");
    const coursesResponse = await getAllCourses({});
    
    // Transform courses to match the expected Course type
    const coursesData: any[] = coursesResponse.data || [];
    const transformedCourses: Course[] = coursesData.map((course: any) => ({
      _id: course._id,
      name: course.title || course.courseIdentifier || "Unnamed Course",
      code: course.courseIdentifier || "COURSE-001",
      teacher: "", // Courses are now global, no teacher field
      createdAt: course.createdAt,
      createdBy: course.createdBy,
    }));
    
    return {
      success: coursesResponse.success,
      data: transformedCourses,
    };
  } catch (error) {
    console.error("Error fetching courses by class:", error);
    return { success: false, data: [] };
  }
}

const getAllCoursesService = async (params?: {
  searchText?: string;
}): Promise<{ success: boolean; data: Course[]; pagination?: PaginationData }> => {
  try {
    // Use the new courses API directly
    const { getAllCourses } = await import("../courses");
    const response = await getAllCourses({
      searchText: params?.searchText,
    });
    
    // Transform courses to match the expected Course type
    const coursesData: any[] = response.data || [];
    const transformedCourses: Course[] = coursesData.map((course: any) => ({
      _id: course._id,
      name: course.title || course.courseIdentifier || "Unnamed Course",
      code: course.courseIdentifier || "COURSE-001",
      teacher: "", // Courses are now global, no teacher field
      createdAt: course.createdAt,
      createdBy: course.createdBy,
    }));
    
    return {
      success: response.success,
      data: transformedCourses,
    };
  } catch (error) {
    console.error("Error fetching all courses:", error);
    return { success: false, data: [] };
  }
}

const getAllAssignments = async ({
  page = 1,
  limit = 10,
  sortBy = "createdAt",
  sortOrder = "desc",
  class: classId,
  course,
  session,
  searchText: search,
  type,
  status,
  branch_id,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  class?: string
  course?: string
  searchText?: string
  type?: string
  status?: string
  session?: string
  branch_id?: string
} = {}): Promise<AssignmentsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ASSIGNMENT_ENDPOINT.ASSIGNMENTS, {
      page,
      limit,
      sortBy,
      sortOrder,
      class: classId,
      searchText: search,
      course,
      session,
      type,
      status,
      branch_id,
    }),
    method: "GET",
  })
  return response.data
}

const getAssignmentById = async (id: string): Promise<AssignmentResponse> => {
  const response = await apiClient({
    url: `${ASSIGNMENT_ENDPOINT.ASSIGNMENTS}/${id}`,
    method: "GET",
  })
  return response.data
}

const createAssignment = async (data: AssignmentCreateData & { file?: File }): Promise<AssignmentResponse> => {
  const formData = new FormData()
  
  Object.keys(data).forEach(key => {
    if (key !== 'file' && data[key as keyof typeof data]) {
      const value = data[key as keyof typeof data] as string;
      console.log(`Adding to formData - ${key}:`, value);
      formData.append(key, value);
    }
  })

  if (data.file) {
    console.log("Adding file to formData");
    formData.append('file', data.file);
  }

  const response = await apiClient({
    url: ASSIGNMENT_ENDPOINT.ASSIGNMENTS,
    method: "POST",
    data: formData,
  })
  
  console.log("API Response:", response);
  return response.data
}

const updateAssignment = async (
  id: string,
  data: AssignmentUpdateData & { file?: File }
): Promise<AssignmentResponse> => {
  const formData = new FormData()
  
  Object.keys(data).forEach(key => {
    if (key !== 'file' && data[key as keyof typeof data]) {
      formData.append(key, data[key as keyof typeof data] as string)
    }
  })

  if (data.file) {
    formData.append('file', data.file)
  }

  const response = await apiClient({
    url: `${ASSIGNMENT_ENDPOINT.ASSIGNMENTS}/${id}`,
    method: "PUT",
    data: formData,
  })
  return response.data
}

const deleteAssignment = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient({
    url: `${ASSIGNMENT_ENDPOINT.ASSIGNMENTS}/${id}`,
    method: "DELETE",
  })
  return response.data
}

const getAllNotes = async ({
  page = 1,
  limit = 10,
  sortBy = "createdAt",
  sortOrder = "desc",
  teacher,
  class: classId,
  searchText: search,
  section,
  course,
  session,
  role,
  branch,
  status = "active",
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  teacher?: string
  class?: string
  searchText?: string
  section?: string
  course?: string
  session?: string
  role?: string
  branch?: string
  status?: string
} = {}): Promise<NotesResponse> => {
  const response = await apiClient({
    url: appendQueryParams(NOTES_ENDPOINT.NOTES, {
      page,
      limit,
      sortBy,
      sortOrder,
      teacher,
      class: classId,
      searchText: search,
      section,
      course,
      session,
      role,
      branch,
      status,
    }),
    method: "GET",
  })
  return response.data
}

const getNoteById = async (id: string): Promise<NoteResponse> => {
  const response = await apiClient({
    url: `${NOTES_ENDPOINT.NOTES}/${id}`,
    method: "GET",
  })
  return response.data
}

const createNote = async (data: NoteCreateData & { file?: File }): Promise<NoteResponse> => {
  const formData = new FormData()
  
  Object.keys(data).forEach(key => {
    if (key !== 'file' && data[key as keyof typeof data]) {
      formData.append(key, data[key as keyof typeof data] as string)
    }
  })

  if (data.file) {
    formData.append('file', data.file)
  }

  const response = await apiClient({
    url: NOTES_ENDPOINT.NOTES,
    method: "POST",
    data: formData,
  })
  return response.data
}

const updateNote = async (
  id: string,
  data: NoteUpdateData & { file?: File }
): Promise<NoteResponse> => {
  const formData = new FormData()
  
  Object.keys(data).forEach(key => {
    if (key !== 'file' && data[key as keyof typeof data]) {
      formData.append(key, data[key as keyof typeof data] as string)
    }
  })

  if (data.file) {
    formData.append('file', data.file)
  }

  const response = await apiClient({
    url: `${NOTES_ENDPOINT.NOTES}/${id}`,
    method: "PUT",
    data: formData,
  })
  return response.data
}

const toggleNoteStatus = async (id: string): Promise<NoteResponse> => {
  const response = await apiClient({
    url: `${NOTES_ENDPOINT.NOTES}/${id}/toggle-status`,
    method: "PUT",
  })
  return response.data
}

const deleteNote = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient({
    url: `${NOTES_ENDPOINT.NOTES}/${id}`,
    method: "DELETE",
  })
  return response.data
}

export {
  getAllAssignments,
  getAssignmentById,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  getAllNotes,
  getNoteById,
  createNote,
  updateNote,
  toggleNoteStatus,
  deleteNote,
  getCoursesByClass,
  getAllCoursesService,
}